<template>

  <div>
    <b-row class="content-header">
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title float-left pr-1 mb-0">
              Editing {{ name }}
            </h1>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-form @submit.prevent="submitForm">
      <validation-observer
        ref="retailItemUpdateForm"
        #default="{ invalid }"
      >
        <b-card header-tag="header">
          <template #header>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              class="primary-button-with-background mr-3"
            >
              <feather-icon
                icon="GridIcon"
                size="20"
              />
            </b-button>
            <h3 class="align-middle mr-auto">
              Retail Item Details
            </h3>
          </template>

          <b-row>
            <b-col md="9">
              <b-form-group
                label="Link to Inventory Item"
                label-for="link-to-inventory-item"
                label-cols-md="3"
              >
                <div class="d-flex align-items-center">
                  <v-select
                    id="link-to-inventory-item"
                    v-model="linkedInventoryItem"
                    class="flex-grow-1"
                    label="name"
                    placeholder="Select from list"
                    :options="inventoryOptions"
                    :clearable="false"
                    @input="onLinkInventoryItem"
                  >
                    <template #list-header>
                      <li class="vs__list-header-label">
                        Inventory items found in Master list
                      </li>
                    </template>
                    <template #option="option">
                      <div class="d-flex align-items-center py-50">
                        <b-img
                          class="mr-1 img-preview-block-sm"
                          :src="option.image ? option.image : defaultImage"
                        />
                        <div>
                          <div>{{ option.name }}</div>
                          <small class="text-muted">{{ option.sku }}</small>
                        </div>
                      </div>
                    </template>
                  </v-select>
                  <b-link
                    v-if="linkedInventoryItem"
                    class="ml-1 text-nowrap"
                    @click="unlinkInventoryItem"
                  >
                    Unlink
                  </b-link>
                </div>
              </b-form-group>

              <validation-provider
                #default="{ errors }"
                name="SKU"
                vid="sku"
                rules="required"
              >
                <b-form-group
                  label="SKU*"
                  label-for="sku"
                  label-cols-md="3"
                  :state="(errors.length > 0 || skuValidation) ? false : null"
                >
                  <b-form-input
                    id="sku"
                    v-model="sku"
                    placeholder="Input SKU number, e.g. URN0-LAVENDER-CRST-LIULI"
                    :disabled="!!linkedInventoryItem"
                    :state="(errors.length > 0 || skuValidation) ? false : null"
                    @input="skuValidation = false"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="skuValidation"
                    class="text-danger"
                  >
                    {{ skuError }}
                  </small>
                </b-form-group>
              </validation-provider>

              <b-form-group
                label="Brand"
                label-for="brand"
                label-cols-md="3"
              >
                <b-form-input
                  id="brand"
                  v-model="brand"
                  placeholder="Input text"
                  :disabled="!!linkedInventoryItem"
                />
              </b-form-group>

              <b-form-group
                v-if="linkedInventoryItem"
                label="Store"
                label-for="store"
                label-cols-md="3"
              >
                <v-select
                  id="store"
                  v-model="store"
                  label="name"
                  :options="storeOptions"
                  :reduce="option => option._id"
                  :clearable="false"
                />
              </b-form-group>
            </b-col>

            <b-col
              v-if="linkedInventoryItem"
              md="3"
              class="text-center"
            >
              <b-img
                class="img-preview-block-lg"
                :src="linkedInventoryItem.image ? linkedInventoryItem.image : defaultImage"
              />
            </b-col>
          </b-row>
        </b-card>

        <b-card header-tag="header">
          <template #header>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              class="primary-button-with-background mr-3"
            >
              <feather-icon
                icon="GridIcon"
                size="20"
              />
            </b-button>
            <h3 class="align-middle mr-auto">
              Display to Customer
            </h3>
          </template>

          <b-row>
            <b-col md="9">
              <b-form-group
                label="Retail Item Image"
                label-cols-md="3"
              >
                <div class="d-flex align-items-center">
                  <div class="image-upload-box mr-1">
                    <b-img
                      v-if="fileSrc"
                      class="image-upload-preview"
                      :src="fileSrc"
                    />
                    <feather-icon
                      v-else
                      icon="ImageIcon"
                      size="20"
                      class="text-muted"
                    />
                  </div>
                  <div>
                    <p
                      v-if="image"
                      class="mb-50"
                    >
                      {{ image.name }}
                    </p>
                    <b-form-file
                      ref="fileInput"
                      v-model="image"
                      type="file"
                      accept="image/jpeg, image/jpg, image/png"
                      style="display: none"
                      @input="setImage"
                    />
                    <b-button
                      variant="primary"
                      @click="$refs.fileInput.$el.childNodes[0].click()"
                    >
                      Upload
                    </b-button>
                  </div>
                </div>
              </b-form-group>

              <validation-provider
                #default="{ errors }"
                name="Retail Item Name"
                vid="name"
                rules="required"
              >
                <b-form-group
                  label="Retail Item Name*"
                  label-for="retail-item-name"
                  label-cols-md="3"
                  :state="(errors.length > 0 || nameValidation) ? false : null"
                >
                  <b-form-input
                    id="retail-item-name"
                    v-model="name"
                    :state="(errors.length > 0 || nameValidation) ? false : null"
                    @input="nameValidation = false"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="nameValidation"
                    class="text-danger"
                  >
                    {{ nameError }}
                  </small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                #default="{ errors }"
                name="Unit Price"
                vid="unitPrice"
                rules="required|min_value:0"
              >
                <b-form-group
                  label="Unit Price*"
                  label-for="unit-price"
                  label-cols-md="3"
                  :state="(errors.length > 0 || unitPriceValidation) ? false : null"
                >
                  <b-input-group
                    prepend="$"
                    class="input-group-merge"
                  >
                    <b-form-input
                      id="unit-price"
                      v-model="unitPrice"
                      type="number"
                      step="0.01"
                      min="0"
                      :state="(errors.length > 0 || unitPriceValidation) ? false : null"
                      @input="unitPriceValidation = false"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="unitPriceValidation"
                    class="text-danger"
                  >
                    {{ unitPriceError }}
                  </small>
                </b-form-group>
              </validation-provider>

              <b-form-group
                label="Category"
                label-for="category"
                label-cols-md="3"
              >
                <v-select
                  id="category"
                  v-model="category"
                  label="name"
                  placeholder="Select from list"
                  :options="categoryOptions"
                  :reduce="option => option._id"
                  @input="onCategoryChange"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-nav class="wrap-border save-nav">
          <li class="nav-item">
            <span>You’re currently editing a retail item. Please save to avoid losing your data.</span>
          </li>
          <li class="nav-item ml-auto">
            <b-button
              type="button"
              variant="flat-primary"
              @click="cancelRedirect"
            >
              Cancel
            </b-button>
          </li>
          <li class="nav-item mr-1">
            <b-button
              type="submit"
              variant="success"
              :disabled="invalid"
            >
              <feather-icon
                icon="SaveIcon"
                class="mr-50"
              />
              <span class="align-middle">Save Changes</span>
            </b-button>
          </li>
        </b-nav>
      </validation-observer>
    </b-form>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BCard, BRow, BCol, BButton, BForm, BFormGroup, BFormInput, BFormFile, BImg, BLink, BNav, BInputGroup,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormFile,
    BImg,
    BLink,
    BNav,
    BInputGroup,
    vSelect,

    ValidationProvider,
    ValidationObserver,
  },
  directives: { Ripple },
  data() {
    return {
      // eslint-disable-next-line global-require
      defaultImage: require('@/assets/images/placeholder/placeholder_image.png'),
      retailItem: {},
      linkedInventoryItem: null,
      sku: '',
      brand: '',
      unitPrice: '',
      store: null,
      image: null,
      fileSrc: '',
      name: '',
      category: null,
      categoryTitle: '',

      nameError: 'Valid Retail Item Name is required',
      nameValidation: false,
      skuError: 'Valid SKU is required',
      skuValidation: false,
      unitPriceError: 'Valid Unit Price is required',
      unitPriceValidation: false,

      storeOptions: [],
      categoryOptions: [],
      inventoryOptions: [],

      required,
    }
  },
  created() {
    this.$http.get(`products/retail-items/${this.$route.params.id}/show`)
      .then(response => {
        const { data } = response.data
        this.retailItem = data
        this.name = data.name || ''
        this.sku = data.sku || ''
        this.brand = data.brand || ''
        this.unitPrice = data.unitPrice ?? ''
        this.fileSrc = data.image || ''
        this.category = data.category ? data.category._id : null
        this.categoryTitle = data.category ? data.category.name : ''
        this.store = data.store ? data.store._id : null
        this.linkedInventoryItem = data.inventoryItem ? {
          _id: data.inventoryItem._id,
          name: data.inventoryItem.name,
          sku: data.inventoryItem.sku,
          image: data.image,
        } : null
      })
      .catch(error => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })

    this.$http.get('inventory/item/respond-with/name-and-id')
      .then(response => {
        this.inventoryOptions = response.data.stores ?? []
      })
      .catch(error => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })

    this.$http.get('inventory/store/respond-with/name-and-id')
      .then(response => {
        this.storeOptions = response.data.stores ?? []
      })
      .catch(error => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })

    this.$http.get('inventory/category/respond-with/name-and-id')
      .then(response => {
        this.categoryOptions = response.data.categories ?? []
      })
      .catch(error => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  },
  methods: {
    onLinkInventoryItem(item) {
      if (!item) return
      this.sku = item.sku
      this.brand = item.brand || ''
      this.unitPrice = item.price ?? ''
      this.store = null
      this.name = item.name
      this.category = item.category || null
      this.categoryTitle = item.categoryTitle || ''
      this.fileSrc = item.image ? item.image : ''
    },
    unlinkInventoryItem() {
      this.linkedInventoryItem = null
      this.sku = ''
      this.brand = ''
      this.store = null
    },
    onCategoryChange(id) {
      const selected = this.categoryOptions.find(option => option._id === id)
      this.categoryTitle = selected ? selected.name : ''
    },
    setImage() {
      if (this.image) {
        if (this.image.size <= 5242880) {
          const reader = new FileReader()
          reader.readAsDataURL(this.image)
          reader.onload = () => {
            this.fileSrc = reader.result
          }
        } else {
          this.fileSrc = ''
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'File size not accepted more than 5MB',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      }
    },
    cancelRedirect() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push({ name: 'products-retail-items-index' })
      }
    },
    submitForm() {
      this.$refs.retailItemUpdateForm.validate().then(async success => {
        if (success) {
          const formData = new FormData()
          formData.append('name', this.name)
          formData.append('sku', this.sku)
          formData.append('brand', this.brand)
          formData.append('unitPrice', this.unitPrice)
          formData.append('category', this.category || '')
          formData.append('categoryTitle', this.categoryTitle)
          formData.append('inventoryItem', this.linkedInventoryItem ? this.linkedInventoryItem._id : '')
          formData.append('store', this.store || '')

          if (this.image) {
            const res = await this.uploadFileToAws(this.image)
            const { fileUrl, fileName, upload } = res || {}
            if (upload && fileUrl) {
              formData.append('image', fileName)
              formData.append('imageStorage', 'aws')
            } else {
              formData.append('itemImage', this.image)
            }
          } else if (this.fileSrc) {
            formData.append('image', this.fileSrc)
            formData.append('imageStorage', this.retailItem.imageStorage || 'aws')
          }

          this.$http.patch(`products/retail-items/${this.$route.params.id}/update`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
            .then(() => {
              this.$swal({
                title: 'Retail Item Updated',
                // eslint-disable-next-line global-require
                imageUrl: require('@/assets/images/icons/success.png'),
                imageWidth: 80,
                imageHeight: 80,
                imageAlt: 'Custom Icon',
                width: '300px',
                showConfirmButton: false,
                showCancelButton: false,
                timer: 1500,
              })
                .then(() => {
                  this.cancelRedirect()
                })
            })
            .catch(error => {
              if (error.data.errors) {
                error.data.errors.forEach(validationError => {
                  if (validationError.param === 'name') {
                    this.nameError = validationError.msg
                    this.nameValidation = true
                  } else if (validationError.param === 'sku') {
                    this.skuError = validationError.msg
                    this.skuValidation = true
                  } else if (validationError.param === 'unitPrice') {
                    this.unitPriceError = validationError.msg
                    this.unitPriceValidation = true
                  }
                })

                const items = document.getElementsByClassName('text-danger')
                window.scrollTo({
                  top: items[0].offsetTop,
                  behavior: 'smooth',
                })
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              }
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.image-upload-box {
  box-sizing: border-box;
  width: 80px;
  height: 80px;
  border: 1px solid #D2DCEA;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.image-upload-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.img-preview-block-sm {
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  border-radius: 5px;
  object-fit: cover;
}
.img-preview-block-lg {
  box-sizing: border-box;
  width: 90px;
  height: 90px;
  border: 1px solid #D2DCEA;
  border-radius: 5px;
  object-fit: cover;
}
</style>

<style lang="scss">
.vs__list-header-label {
  padding: 8px 10px 4px;
  font-weight: 600;
  color: #6e6b7b;
  pointer-events: none;
}
</style>
